/**
 * Feature Style Resolver
 *
 * Resolves per-feature styles based on the layer's symbology configuration.
 * For categorized and graduated symbology, each feature may have a different
 * style depending on its attribute values.
 *
 * This utility is used by the attribute-map sync mechanism to recalculate
 * feature styles in real-time when attribute values change.
 *
 * **Validates: Requirement 13.5** - WHEN a Registered_User edits attributes,
 * THE Attribute_Table_Viewer SHALL immediately reflect changes in the feature properties.
 */

import { Style, Fill, Stroke, Circle, RegularShape } from 'ol/style';
import type { Symbology, CategorizedRule, GraduatedRule, MarkerSymbol } from '../types/symbology';

/**
 * Converts a hex color to RGBA format with opacity
 */
function hexToRgba(hexColor: string, opacity: number): string {
  let hex = hexColor.replace('#', '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Creates an OpenLayers image style for a point marker symbol.
 */
function createMarkerImage(
  markerSymbol: MarkerSymbol,
  radius: number,
  fillColor: string,
  strokeColor: string,
  strokeWidth: number
): Circle | RegularShape {
  const fill = new Fill({ color: fillColor });
  const stroke = new Stroke({ color: strokeColor, width: strokeWidth });

  switch (markerSymbol) {
    case 'circle':
      return new Circle({ radius, fill, stroke });
    case 'square':
      return new RegularShape({ points: 4, radius, angle: Math.PI / 4, fill, stroke });
    case 'triangle':
      return new RegularShape({ points: 3, radius, angle: 0, fill, stroke });
    case 'star':
      return new RegularShape({ points: 5, radius, radius2: radius * 0.4, angle: 0, fill, stroke });
    case 'cross':
      return new RegularShape({
        points: 4,
        radius,
        radius2: 0,
        angle: 0,
        fill: new Fill({ color: strokeColor }),
        stroke,
      });
    case 'x':
      return new RegularShape({
        points: 4,
        radius,
        radius2: 0,
        angle: Math.PI / 4,
        fill: new Fill({ color: strokeColor }),
        stroke,
      });
    default:
      return new Circle({ radius, fill, stroke });
  }
}

/**
 * Creates an OpenLayers Style from a SymbolStyle rule configuration.
 */
function createStyleFromRule(
  rule: {
    fillColor?: string;
    strokeColor?: string;
    strokeWidth?: number;
    opacity?: number;
    size?: number;
  },
  geometryType: 'Point' | 'LineString' | 'Polygon',
  defaultOpacity: number = 1
): Style {
  const fillColor = rule.fillColor || '#3388ff';
  const strokeColor = rule.strokeColor || (geometryType === 'LineString' ? '#3388ff' : '#ffffff');
  const strokeWidth = rule.strokeWidth ?? (geometryType === 'LineString' ? 3 : 2);
  const opacity = rule.opacity ?? defaultOpacity;
  const size = rule.size || 8;

  const fillRgba = hexToRgba(fillColor, opacity);
  const strokeRgba = hexToRgba(strokeColor, opacity);

  const styleConfig: any = {
    fill: new Fill({ color: fillRgba }),
    stroke: new Stroke({ color: strokeRgba, width: strokeWidth }),
  };

  if (geometryType === 'Point') {
    styleConfig.image = createMarkerImage('circle', size, fillRgba, strokeRgba, strokeWidth);
  }

  return new Style(styleConfig);
}

/**
 * Determines if a layer's symbology depends on a specific attribute field.
 * Returns true if the symbology uses categorized or graduated rules that
 * reference the given field.
 *
 * @param symbology - The layer's symbology configuration
 * @param field - The attribute field name that was changed
 * @returns true if the symbology depends on the given field
 */
export function symbologyDependsOnField(symbology: Symbology | undefined, field: string): boolean {
  if (!symbology) return false;

  if (symbology.type === 'categorized' && symbology.categorizedRules) {
    return symbology.categorizedRules.some((rule) => rule.attributeField === field);
  }

  if (symbology.type === 'graduated' && symbology.graduatedRules) {
    return symbology.graduatedRules.some((rule) => rule.attributeField === field);
  }

  return false;
}

/**
 * Resolves the style for a specific feature based on the layer's symbology
 * and the feature's current attribute values.
 *
 * For simple symbology, returns null (feature should use the layer-level style).
 * For categorized/graduated symbology, returns a per-feature Style based on
 * the feature's attribute values.
 *
 * @param symbology - The layer's symbology configuration
 * @param properties - The feature's current properties/attributes
 * @param geometryType - The geometry type of the layer
 * @returns An OpenLayers Style for the feature, or null if the layer-level style should be used
 */
export function resolveFeatureStyle(
  symbology: Symbology | undefined,
  properties: Record<string, any>,
  geometryType: 'Point' | 'LineString' | 'Polygon'
): Style | null {
  if (!symbology) return null;

  if (symbology.type === 'categorized' && symbology.categorizedRules) {
    return resolveCategorizedStyle(
      symbology.categorizedRules,
      properties,
      geometryType,
      symbology.opacity
    );
  }

  if (symbology.type === 'graduated' && symbology.graduatedRules) {
    return resolveGraduatedStyle(
      symbology.graduatedRules,
      properties,
      geometryType,
      symbology.opacity
    );
  }

  // Simple symbology — feature uses the layer-level style
  return null;
}

/**
 * Resolves style for categorized symbology by matching the feature's attribute
 * value against the categorized rules.
 */
function resolveCategorizedStyle(
  rules: CategorizedRule[],
  properties: Record<string, any>,
  geometryType: 'Point' | 'LineString' | 'Polygon',
  defaultOpacity?: number
): Style | null {
  for (const rule of rules) {
    const featureValue = properties[rule.attributeField];
    // Use loose equality to handle string/number coercion
    if (featureValue == rule.value) {
      return createStyleFromRule(rule.symbol, geometryType, defaultOpacity);
    }
  }
  // No matching rule — return null to use layer default
  return null;
}

/**
 * Resolves style for graduated symbology by checking if the feature's numeric
 * attribute value falls within any of the graduated ranges.
 */
function resolveGraduatedStyle(
  rules: GraduatedRule[],
  properties: Record<string, any>,
  geometryType: 'Point' | 'LineString' | 'Polygon',
  defaultOpacity?: number
): Style | null {
  for (const rule of rules) {
    const featureValue = Number(properties[rule.attributeField]);
    if (!isNaN(featureValue) && featureValue >= rule.minValue && featureValue <= rule.maxValue) {
      return createStyleFromRule(rule.symbol, geometryType, defaultOpacity);
    }
  }
  // No matching rule — return null to use layer default
  return null;
}
